import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import type { PropertyFolder } from "../../models/propertyFolder";
import styles from "./PropertyMoveToFolderDialog.module.css";

export interface MoveToFolderTarget {
  id: number;
  type: "property" | "folder";
  currentFolderId: number | null;
}

interface PropertyMoveToFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: MoveToFolderTarget;
  /** Reçoit l'id du dossier de destination, ou null pour retirer l'affectation. */
  onMove: (folderId: number | null) => Promise<void>;
}

interface FolderOption {
  value: number;
  label: string;
}

/* Un dossier ne peut pas être déplacé dans lui-même ni dans un de ses
   descendants (cela créerait un cycle). */
function collectExcludedFolderIds(
  target: MoveToFolderTarget,
  folders: PropertyFolder[],
): Set<number> {
  const excluded = new Set<number>();
  if (target.type !== "folder") return excluded;
  excluded.add(target.id);
  let addedMore = true;
  while (addedMore) {
    addedMore = false;
    for (const folder of folders) {
      if (
        folder.id_parent &&
        excluded.has(folder.id_parent) &&
        !excluded.has(folder.id)
      ) {
        excluded.add(folder.id);
        addedMore = true;
      }
    }
  }
  return excluded;
}

export default function PropertyMoveToFolderDialog({
  open,
  onOpenChange,
  target,
  onMove,
}: PropertyMoveToFolderDialogProps) {
  const { propertyFolders, propertyFoldersById } = usePropertyFolders({
    withById: true,
  }) as {
    propertyFolders: PropertyFolder[];
    propertyFoldersById: Record<number, PropertyFolder>;
  };
  const [selected, setSelected] = useState<FolderOption | null>(null);
  const [error, setError] = useState<ReactNode>(null);
  const [pending, setPending] = useState<"move" | "remove" | null>(null);

  const currentFolder = target.currentFolderId
    ? propertyFoldersById[target.currentFolderId]
    : undefined;

  const excludedIds = collectExcludedFolderIds(target, propertyFolders);
  const options: FolderOption[] = propertyFolders
    .filter(
      (folder) =>
        folder.id !== target.currentFolderId && !excludedIds.has(folder.id),
    )
    .map((folder) => ({ value: folder.id, label: folder.nom }));

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSelected(null);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function submit(folderId: number | null) {
    setError(null);
    setPending(folderId === null ? "remove" : "move");
    try {
      await onMove(folderId);
      handleOpenChange(false);
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setPending(null);
    }
  }

  const itemLabel = target.type === "folder" ? "ce dossier" : "ce bien";

  return (
    <ModalDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="sm"
      maxContentWidth="md"
      header="Affecter à un immeuble"
      subtitle={`Rattachez ${itemLabel} à un immeuble ou un dossier pour le regrouper dans votre gestion.`}
    >
      <ModalDialogBody className={styles.container}>
        {currentFolder && (
          <Callout variant="neutral" title="Affectation actuelle">
            {currentFolder.nom}
          </Callout>
        )}

        {options.length > 0 ? (
          <ReactSelectStandalone
            label="Choisir un immeuble ou un dossier"
            placeholder="Choisissez un dossier"
            options={options}
            value={selected}
            onChange={(option: FolderOption | null) => setSelected(option)}
          />
        ) : (
          <Callout variant="info">
            Vous n'avez aucun {currentFolder ? "autre " : ""}dossier disponible.
            Vous pouvez en créer un dans la rubrique{" "}
            <Link to="/bailleur/properties/add">Ajouter un bien</Link>.
          </Callout>
        )}

        <Callout variant="danger">{error}</Callout>
      </ModalDialogBody>

      <ModalDialogFooter
        start={
          currentFolder && (
            <Button
              variant="danger"
              isLoading={pending === "remove"}
              disabled={pending !== null}
              onClick={() => {
                void submit(null);
              }}
            >
              Supprimer l'affectation
            </Button>
          )
        }
      >
        <Button
          disabled={!selected || pending !== null}
          isLoading={pending === "move"}
          onClick={() => {
            if (selected) void submit(selected.value);
          }}
        >
          Affecter
        </Button>
      </ModalDialogFooter>
    </ModalDialog>
  );
}
